import { ensureCognitoUser, requestMagicLink } from "@src/appV2/Auth/cognito/cognitoAuth";
import { mapCognitoError } from "@src/appV2/Auth/cognito/mapCognitoError";
import {
  classifyCognitoError,
  IDENTITY_PROVIDER,
  isNetworkFailure,
  logAuthRedirectFailure,
  LOGIN_OUTCOME,
  LOGIN_REASON,
  setLoginIdentityProvider,
} from "@src/appV2/Auth/loginRum";

interface SendLoginEmailParams {
  email: string;
  requestedURL?: string | undefined;
  /**
   * Skip the `ensureCognitoUser` JIT-import step. Set this on a resend: the
   * user was already imported on the initial send, so re-running it just fires
   * the PreSignUp Lambda again for no benefit (DEVOP-5526). Leave it off for
   * the first send so legacy users are still imported.
   */
  skipEnsureUser?: boolean;
}

export type SendLoginEmailResult = { success: true } | { success: false; error: string };

/**
 * Sends a Cognito magic link login email.
 *
 * Calls `ensureCognitoUser` first so legacy workplace users (who exist in
 * `facilityusers` but not yet in the Cognito user pool) are JIT-imported
 * before the CUSTOM_AUTH flow runs. CUSTOM_AUTH does not invoke the
 * PreAuthentication Lambda, so the import has to be triggered from the
 * client — without this, pre-existing users hit "No registered user found"
 * on first login attempt in the redesigned app.
 *
 * On a resend (`skipEnsureUser`) the import is skipped — the initial send
 * already ran it, and it would only re-trigger the PreSignUp Lambda.
 */
export async function sendLoginEmail({
  email,
  skipEnsureUser = false,
}: SendLoginEmailParams): Promise<SendLoginEmailResult> {
  setLoginIdentityProvider(IDENTITY_PROVIDER.COGNITO_MAGIC_LINK);

  try {
    if (!skipEnsureUser) {
      await ensureCognitoUser(email);
    }
    await requestMagicLink(email);
    return { success: true };
  } catch (error: unknown) {
    const mappedError = mapCognitoError(error, "magic-link-request");
    const { outcome, reason } =
      mappedError.code === "auth/unknown" && isNetworkFailure(error)
        ? { outcome: LOGIN_OUTCOME.EXPECTED_ERROR, reason: LOGIN_REASON.NETWORK_FAILURE }
        : classifyCognitoError(mappedError.code);
    logAuthRedirectFailure({
      error,
      metadata: {
        errorCode: mappedError.code,
        errorMessage: mappedError.message,
        email,
        outcome,
        reason,
      },
    });
    return { success: false, error: mappedError.message };
  }
}
